import { BILLING_CURRENCY } from './../../billing/constants';
import { AmountObject, ById } from 'common/types';
import { constrain } from 'common/utils/math';
import { multiplyPrice } from 'common/utils/pricing';
import moment from 'moment-timezone';
import { getSubscriptionPrice, PurchasedPlan } from '../plans';
import { LocationSubscription, Subscription } from './types';
import { minBy } from 'lodash';

export const isLastDayOfMonth = (date: moment.Moment) => date.daysInMonth() === date.date();

export const calculateUpcomingSubscriptionDate = (date: moment.Moment, billingCycle: number) =>
	isLastDayOfMonth(date)
		? date.clone().add(billingCycle, 'month').endOf('month')
		: date.clone().add(billingCycle, 'month');

export const calculatePreviousSubscriptionDate = (date: moment.Moment, billingCycle: number) =>
	isLastDayOfMonth(date)
		? date.clone().subtract(billingCycle, 'month').endOf('month')
		: date.clone().subtract(billingCycle, 'month');

export const getSubscriptionDates = (dateFrom: moment.Moment, billingCycle: number) => {
	const previousBillingDate = moment.utc(dateFrom).startOf('day');
	const upcomingBillingDate = calculateUpcomingSubscriptionDate(previousBillingDate, billingCycle);
	return {
		previousBillingDate: previousBillingDate.format('YYYY-MM-DD'),
		upcomingBillingDate: upcomingBillingDate.format('YYYY-MM-DD'),
	};
};

export const getLocationSubscription = (
	purchasedPlan: PurchasedPlan,
	locationId: string,
): LocationSubscription | null => {
	return purchasedPlan.subscription?.locations[locationId] ?? null;
};

export const getLocationSubscriptionPrice = (
	purchasedPlan: PurchasedPlan,
	locationId: string,
): AmountObject => {
	const subscription = getLocationSubscription(purchasedPlan, locationId);
	if (!subscription) return { value: 0, currency: BILLING_CURRENCY.code };
	return (
		getSubscriptionPrice(purchasedPlan, subscription)?.fixed ?? {
			value: 0,
			currency: BILLING_CURRENCY.code,
		}
	);
};

export const getNewSubscription = (args: {
	startDate: moment.Moment;
	locationIds: string[];
	subscriptionDates?: {
		previousBillingDate: string;
		upcomingBillingDate: string;
	};
}): Subscription => {
	const { startDate, locationIds, subscriptionDates } = args;
	const { previousBillingDate, upcomingBillingDate } =
		subscriptionDates ?? getSubscriptionDates(startDate, 1);

	return locationIds.reduce(
		(result, locationId) => {
			result.locations[locationId] = getNewLocationSubscription({
				startDate,
				subscriptionDates,
			});

			return result;
		},
		{
			upcomingBillingDate,
			previousBillingDate,
			billingCycle: 1,
			locations: {},
		} as Subscription,
	);
};

export const getNewLocationSubscription = (args: {
	startDate: moment.Moment;
	subscriptionDates?: {
		previousBillingDate: string;
		upcomingBillingDate: string;
	};
}): LocationSubscription => {
	const { startDate, subscriptionDates } = args;

	const { previousBillingDate, upcomingBillingDate } =
		subscriptionDates ?? getSubscriptionDates(startDate, 1);
	return {
		startDate: moment.utc(startDate).format('YYYY-MM-DD'),
		billingCycle: 1,
		previousBillingDate,
		previousBillingInfo: {
			refundableAmount: {
				value: 0,
				currency: BILLING_CURRENCY.code,
			},
			hasRevenueDiscount: false,
		},
		upcomingBillingDate,
		pricing: {
			type: 'default',
		},
	};
};

const getPassedCycleDays = (
	subscription: Subscription | LocationSubscription,
	date: moment.Moment,
) => Math.max(date.diff(moment.utc(subscription.previousBillingDate).startOf('day'), 'days'), 0);

const getPassedCycleDaysFromStartDate = (subscription: LocationSubscription, date: moment.Moment) =>
	Math.max(date.diff(moment.utc(subscription.startDate).startOf('day'), 'days'), 0);

const getTotalCycleDays = (subscription: Subscription | LocationSubscription) =>
	moment
		.utc(subscription.upcomingBillingDate)
		.startOf('day')
		.diff(moment.utc(subscription.previousBillingDate).startOf('day'), 'days');

const getTotalCycleDaysFromStartDate = (subscription: LocationSubscription) =>
	moment
		.utc(subscription.upcomingBillingDate)
		.startOf('day')
		.diff(moment.utc(subscription.startDate).startOf('day'), 'days');

export const getCycleProgression = (
	subscription: Subscription | LocationSubscription,
	date: moment.Moment,
) => {
	const totalCycleDays = getTotalCycleDays(subscription);
	const passedDays = getPassedCycleDays(subscription, date);

	return constrain(0, 1)(passedDays / totalCycleDays);
};

export const getCycleProgressionFromStartDate = (
	subscription: LocationSubscription,
	date: moment.Moment,
) => {
	const totalCycleDays = getTotalCycleDaysFromStartDate(subscription);
	const passedDays = getPassedCycleDaysFromStartDate(subscription, date);

	return constrain(0, 1)(passedDays / totalCycleDays);
};

export const getUpdatedSubscriptionObject = (
	subscriptions: ById<LocationSubscription>,
): Subscription | null => {
	const subscriptionsArray: LocationSubscription[] = Object.values(subscriptions);
	if (subscriptionsArray.length === 0) return null;
	return {
		previousBillingDate: minBy(subscriptionsArray, (s) => s.previousBillingDate)!
			.previousBillingDate,
		upcomingBillingDate: minBy(subscriptionsArray, (s) => s.upcomingBillingDate)!
			.upcomingBillingDate,
		billingCycle: 1,
		locations: subscriptions,
	};
};

export const getSubscriptionBillingCycle = (subscription: Subscription | LocationSubscription) => ({
	start: subscription.previousBillingDate,
	end: subscription.upcomingBillingDate,
});

/**
 * Get the amount of refund credits to give out when cancelling a subscription, or changing to another subscription
 */
export const getSubscriptionCancelRefund = (args: {
	plan: PurchasedPlan;
	locationId: string;
	cancellationDate: moment.Moment;
}): AmountObject => {
	const { plan, locationId, cancellationDate } = args;

	const subscription = getLocationSubscription(plan, locationId);
	if (!subscription) return { value: 0, currency: BILLING_CURRENCY.code };

	const daysPassedPercentage = getCycleProgressionFromStartDate(
		subscription,
		moment.utc(cancellationDate),
	);
	const daysRemainingPercentage = 1 - daysPassedPercentage;
	const { refundableAmount } = subscription.previousBillingInfo;

	return multiplyPrice(refundableAmount, daysRemainingPercentage);
};

/**
 * Get the amount of discount to give when changing to a new plan midway through a billing cycle
 */

export const getSubscriptionStartDiscountFromLocationId = (args: {
	plan: PurchasedPlan;
	startDate: moment.Moment;
	locationId: string;
}): AmountObject => {
	const { plan, locationId, startDate } = args;

	const subscription = getLocationSubscription(plan, locationId);
	if (!subscription) return { value: 0, currency: BILLING_CURRENCY.code };
	const subscriptionPrice = getSubscriptionPrice(plan, subscription)?.fixed;
	const daysPassedPercentage = getCycleProgression(subscription, moment.utc(startDate));

	if (!subscriptionPrice || daysPassedPercentage === 1)
		return { value: 0, currency: BILLING_CURRENCY.code };

	return multiplyPrice(subscriptionPrice, daysPassedPercentage);
};

export const getSubscriptionStartDiscountFromSubscription = (args: {
	plan: PurchasedPlan;
	startDate: moment.Moment;
	subscription: LocationSubscription | null;
}): AmountObject => {
	const { plan, subscription, startDate } = args;

	if (!subscription) return { value: 0, currency: BILLING_CURRENCY.code };
	const subscriptionPrice = getSubscriptionPrice(plan, subscription)?.fixed;
	if (!subscriptionPrice) return { value: 0, currency: BILLING_CURRENCY.code };

	const daysPassedPercentage = getCycleProgression(subscription, moment.utc(startDate));
	return multiplyPrice(subscriptionPrice, daysPassedPercentage);
};
